/**
 * Chart Tooltips Mixin
 * Provides reusable tooltip functionality for chart components
 * Based on the sophisticated tooltip logic from DataExplorer.vue
 */

export const chartTooltipsMixin = {
  data() {
    return {
      currentTooltip: null,
      currentCurveInfo: null,
      tooltipHandlers: null
    }
  },
  
  methods: {
    /**
     * Setup custom tooltip handling for the chart
     * @param {Object} chart - Chart.js instance
     * @param {Array} curveFeatures - Array of curve features for proximity detection
     * @param {Function} getTooltipContent - Function to generate tooltip content
     */
    setupChartTooltips(chart, curveFeatures, getTooltipContent) {
      if (!chart || !chart.canvas) {
        console.warn('Chart or canvas not available for tooltip setup')
        return
      }
      
      const canvas = chart.canvas
      
      const handleMouseMove = (event) => {
        if (!chart || !chart.chartArea || !curveFeatures?.length) return
        
        const rect = canvas.getBoundingClientRect()
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top
        
        // Convert pixel coordinates to chart coordinates
        if (!chart.scales || !chart.scales.x || !chart.scales.y) return
        
        const xValue = chart.scales.x.getValueForPixel(x)
        const yValue = chart.scales.y.getValueForPixel(y)
        
        if (xValue === null || yValue === null) return
        
        // Ensure xValue is a Date object before calling getTime()
        let currentTime
        try {
          if (xValue instanceof Date) {
            currentTime = xValue.getTime()
          } else if (typeof xValue === 'string') {
            currentTime = new Date(xValue).getTime()
          } else if (typeof xValue === 'number') {
            currentTime = xValue
          } else {
            console.warn('Unexpected xValue type:', typeof xValue, xValue)
            return
          }
          
          if (isNaN(currentTime)) {
            console.warn('Invalid currentTime:', currentTime)
            return
          }
        } catch (error) {
          console.warn('Error processing xValue:', error, xValue)
          return
        }
        
        // Find if we're near a curve line
        let foundCurve = null
        let foundType = null
        
        curveFeatures.forEach((curve, index) => {
          if (curve.begin_CURVE && curve.end_CURVE) {
            const startTime = new Date(curve.begin_CURVE).getTime()
            const endTime = new Date(curve.end_CURVE).getTime()
            
            // Check if we're near start line (within 5 minutes)
            if (Math.abs(currentTime - startTime) < 5 * 60 * 1000) {
              foundCurve = { index, curve }
              foundType = 'start'
            }
            // Check if we're near end line (within 5 minutes)
            else if (Math.abs(currentTime - endTime) < 5 * 60 * 1000) {
              foundCurve = { index, curve }
              foundType = 'end'
            }
          }
        })
        
        // Show or update tooltip
        if (foundCurve && foundType) {
          if (!this.currentTooltip) {
            this.currentTooltip = document.createElement('div')
            this.currentTooltip.className = 'chart-curve-tooltip'
            this.currentTooltip.style.cssText = `
              position: absolute;
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 8px 12px;
              border-radius: 4px;
              font-size: 12px;
              font-family: Arial, sans-serif;
              pointer-events: none;
              z-index: 1000;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
              white-space: nowrap;
            `
            document.body.appendChild(this.currentTooltip)
          }
          
          // Get tooltip content from the provided function
          const tooltipContent = getTooltipContent ? 
            getTooltipContent(foundCurve, foundType, xValue, yValue) : 
            `Curve ${foundCurve.index + 1} ${foundType === 'start' ? 'Start' : 'End'}`
          
          if (this.currentTooltip.textContent !== tooltipContent) {
            this.currentTooltip.textContent = tooltipContent
          }
          
          this.currentTooltip.style.left = (event.clientX + 10) + 'px'
          this.currentTooltip.style.top = (event.clientY - 30) + 'px'
          this.currentTooltip.style.display = 'block'
          
          this.currentCurveInfo = { index: foundCurve.index, type: foundType }
        } else {
          // Hide tooltip if not near any curve line
          if (this.currentTooltip) {
            this.currentTooltip.style.display = 'none'
            this.currentCurveInfo = null
          }
        }
      }
      
      const handleMouseLeave = () => {
        if (this.currentTooltip) {
          this.currentTooltip.style.display = 'none'
          this.currentCurveInfo = null
        }
      }
      
      // Add event listeners
      canvas.addEventListener('mousemove', handleMouseMove)
      canvas.addEventListener('mouseleave', handleMouseLeave)
      
      // Store references for cleanup
      this.tooltipHandlers = { handleMouseMove, handleMouseLeave, canvas }
    },
    
    /**
     * Clean up tooltip event listeners and DOM elements
     */
    cleanupTooltips() {
      if (this.tooltipHandlers) {
        const { handleMouseMove, handleMouseLeave, canvas } = this.tooltipHandlers
        canvas.removeEventListener('mousemove', handleMouseMove)
        canvas.removeEventListener('mouseleave', handleMouseLeave)
        this.tooltipHandlers = null
      }
      
      if (this.currentTooltip) {
        if (this.currentTooltip.parentNode) {
          this.currentTooltip.parentNode.removeChild(this.currentTooltip)
        }
        this.currentTooltip = null
      }
      
      this.currentCurveInfo = null
    }
  },
  
  beforeUnmount() {
    this.cleanupTooltips()
  }
}
